// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Toast, ToastContext, ToastOptions } from '@/contexts/toast-context';
import { Alert, Fade, Snackbar, SnackbarCloseReason } from '@mui/material';
import { SnackbarOrigin } from '@mui/material/Snackbar';
import {
  PropsWithChildren,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';

const DEFAULT_TOAST_DURATION = 10000;
const DEFAULT_TOAST_ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'right',
} as const satisfies SnackbarOrigin;
const TOAST_MARGIN_PX = 20;
const TOAST_STACK_OFFSET_PX = 70;

function getToastAnchorOrigin(toast: Toast): SnackbarOrigin {
  return toast.anchorOrigin ?? DEFAULT_TOAST_ANCHOR_ORIGIN;
}

function getToastAnchorKey(toast: Toast): string {
  const anchorOrigin = getToastAnchorOrigin(toast);
  return `${anchorOrigin.vertical}-${anchorOrigin.horizontal}`;
}

function getToastStackIndex(toasts: Toast[], toastIndex: number): number {
  const toast = toasts[toastIndex];
  if (toast === undefined) {
    return 0;
  }

  const anchorKey = getToastAnchorKey(toast);
  return toasts
    .slice(0, toastIndex)
    .filter((candidate) => getToastAnchorKey(candidate) === anchorKey).length;
}

function getToastSnackbarSx(anchorOrigin: SnackbarOrigin, stackIndex: number) {
  const offsetPx = TOAST_MARGIN_PX + stackIndex * TOAST_STACK_OFFSET_PX;

  return {
    ...(anchorOrigin.vertical === 'top'
      ? { marginTop: `${offsetPx}px` }
      : { marginBottom: `${offsetPx}px` }),
    ...(anchorOrigin.horizontal === 'left'
      ? { marginLeft: `${TOAST_MARGIN_PX}px` }
      : anchorOrigin.horizontal === 'right'
        ? { marginRight: `${TOAST_MARGIN_PX}px` }
        : {}),
  };
}

/**
 * A provider component for managing and displaying toast notifications.
 * It uses context to handle toast-related operations such as adding and removing toasts.
 *
 * @example
 * ```tsx
 * const { showToast } = useToast();
 *
 * const handleShowToast = () => {
 *   showToast({
 *     severity: "success",  // or "error", "warning", "info"
 *     element: <span>Toast message goes here!</span>,
 *     duration: 3000,      // Auto-hide after 3 seconds
 *     sx: { backgroundColor: "green" }  // Custom styles for the toast
 *   });
 * };
 *
 * return (
 *   <ToastProvider>
 *     <div>
 *       <button onClick={handleShowToast}>Show Toast</button>
 *     </div>
 *   </ToastProvider>
 * );
 * ```
 *
 * @param props - The props passed to the ToastProvider, including children components.
 * @param {PropsWithChildren} props.children - The child components to be wrapped by the provider.
 * @returns {JSX.Element} The ToastProvider component wrapping its children and managing toast notifications.
 */
export function ToastProvider(props: PropsWithChildren) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Track open state for all toasts in a single state object
  const [openToasts, setOpenToasts] = useState<Record<string, boolean>>({});
  const actions = useMemo(
    () => ({
      showToast: (options: ToastOptions) => {
        const id = Date.now().toString();
        const duration = options.duration ?? DEFAULT_TOAST_DURATION;
        const expiresAt = Date.now() + duration;
        setToasts((prevToasts) => [
          ...prevToasts,
          { id, expiresAt, ...options },
        ]);
        // Set initial open state for new toast
        setOpenToasts((prev) => ({ ...prev, [id]: true }));
      },
      removeToast: (id: string) => {
        setToasts((prevToasts) =>
          prevToasts.filter((toast) => toast.id !== id)
        );
        // Clean up open state when toast is removed
        setOpenToasts((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      },
    }),
    []
  );

  // Check for expired toasts using absolute timestamps
  // This ensures toasts expire reliably regardless of window focus or browser throttling
  useEffect(() => {
    if (toasts.length === 0) {
      return;
    }

    const intervalId = setInterval(() => {
      const now = Date.now();
      toasts.forEach((toast) => {
        if (now >= toast.expiresAt && openToasts[toast.id]) {
          setOpenToasts((prev) => ({ ...prev, [toast.id]: false }));
        }
      });
    }, 100);

    return () => clearInterval(intervalId);
  }, [toasts, openToasts]);

  const handleClose =
    (id: string) =>
    (_event?: SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      if (reason === 'clickaway') {
        return;
      }
      setOpenToasts((prev) => ({ ...prev, [id]: false }));
    };

  return (
    <ToastContext.Provider value={actions}>
      {props.children}
      {toasts.map((toast, index) => {
        // Extract only valid Alert props, excluding internal toast tracking fields
        const {
          id,
          expiresAt: _expiresAt,
          element,
          duration: _duration,
          anchorOrigin: toastAnchorOrigin,
          sx,
          ...alertProps
        } = toast;
        const anchorOrigin = toastAnchorOrigin ?? DEFAULT_TOAST_ANCHOR_ORIGIN;
        const stackIndex = getToastStackIndex(toasts, index);
        const toastStyles = {
          minWidth: '300px',
          ...sx,
        };

        return (
          <Snackbar
            anchorOrigin={anchorOrigin}
            open={openToasts[id]}
            onClose={handleClose(id)}
            key={id}
            TransitionProps={{
              onExited: () => actions.removeToast(id),
              timeout: 750,
            }}
            TransitionComponent={Fade}
            sx={getToastSnackbarSx(anchorOrigin, stackIndex)}
          >
            <Alert
              icon={false}
              {...alertProps}
              onClose={handleClose(id)}
              variant={'standard'}
              sx={toastStyles}
            >
              {element}
            </Alert>
          </Snackbar>
        );
      })}
    </ToastContext.Provider>
  );
}
